// The instructions for whoever maintains the website, as plain text to copy: what happens, where, on which
// device and when, the evidence, how to fix it and how to check it. Everything in it is about one element;
// it says outright that copying it, or applying the change, does not close the problem.

export interface InstructionsInput {
  t: (key: string, vars?: Record<string, string | number>) => string;
  problem: { title: string; priority: string; criteria: string[] };
  element: { title: string; zone: string | null };
  page: { title: string; url: string };
  device: string;
  /** When the analysis ran, as the person reads it. */
  date: string;
  what: string;
  evidence: { label: string; value: string }[];
  steps: string[];
  check: string[];
  /** The change proposed, when there is one. */
  code: { status: 'adapted' | 'example'; before: string; after: string; explanation: string } | null;
  /** The element's technical record. */
  selector: string;
  html: string;
  position: string | null;
}

const list = (items: string[]) => items.map((item, index) => `${index + 1}. ${item}`).join('\n');
const indent = (code: string) =>
  code
    .split('\n')
    .map((line) => `    ${line}`)
    .join('\n');

export function elementInstructions(i: InstructionsInput): string {
  const { t } = i;
  const parts: string[] = [
    t('element.copy.title'),
    '',
    `${t('element.copy.problem')}: ${i.problem.title} (${i.problem.priority})${i.problem.criteria.length > 0 ? ` · WCAG ${i.problem.criteria.join(', ')}` : ''}`,
    `${t('element.copy.what')}: ${i.what}`,
    '',
    `${t('element.copy.where')}:`,
    `- ${t('element.copy.element')}: ${i.element.title}${i.element.zone ? ` — ${i.element.zone}` : ''}`,
    `- ${t('element.copy.page')}: ${i.page.title} (${i.page.url})`,
    `- ${t('element.copy.device')}: ${i.device}`,
    ...(i.position ? [`- ${t('element.copy.position')}: ${i.position}`] : []),
    `- ${t('element.copy.date')}: ${i.date}`,
  ];
  if (i.evidence.length > 0)
    parts.push(
      '',
      `${t('element.copy.evidence')}:`,
      ...i.evidence.map((line) => `- ${line.label}: ${line.value}`),
    );
  if (i.steps.length > 0) parts.push('', `${t('element.copy.how')}:`, list(i.steps));
  parts.push('', `${t('element.copy.currentCode')}:`, indent(i.html));
  if (i.code) {
    parts.push(
      '',
      `${t(i.code.status === 'adapted' ? 'element.proposal.adapted' : 'element.proposal.example')}:`,
      indent(i.code.after),
      i.code.explanation,
    );
  }
  if (i.check.length > 0) parts.push('', `${t('element.copy.check')}:`, list(i.check));
  parts.push('', `${t('element.copy.selector')}: ${i.selector}`, '', t('element.copy.note'));
  return parts.join('\n');
}
